import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Select,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  GrantRole,
  OrganizationRoleType,
  useAssignDepartmentRoleMutation,
  useListDepartmentsQuery,
  useListOrganizationUsersQuery,
  useUpdateOrgUserRoleMutation,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { supersededOrganizationRoles } from "./organizationRoleSupersession";
import { asGrantRole, asOrganizationRole } from "./roleVocabulary";
import { SettingsCard } from "./SettingsCard";
import {
  departmentAssignableRoles,
  organizationAssignableRoles,
} from "./useDepartmentRoles";
import { useGrantRoleName } from "./useGrantRoleName";

// t('{{person}} will have the role {{role}}.') t('{{person}} will have the role {{role}} in {{where}}.')

interface Person {
  id: string;
  name: string;
  /** Never re-fetched here: updateOrgUserRole replaces the whole set, so a half-loaded query revokes the rest. */
  organizationRoles: OrganizationRoleType[];
}

interface Props {
  person?: Person;
  departmentId?: string;
  onDone: () => void;
}

export const GiveAccessForm = ({ person, departmentId, onDone }: Props) => {
  const authState = useLoggedInWithOrgContextState();
  const orgId = authState.selectedOrg.id;
  const { t } = useTranslation();
  const toast = useToast();
  const displayer = useApiError();
  const roleName = useGrantRoleName();

  const { data } = useListDepartmentsQuery({ orgId });
  const { data: userData } = useListOrganizationUsersQuery(
    { skip: 0, limit: 100 },
    { skip: !!person },
  );
  const [assign, { isLoading: isAssigning }] =
    useAssignDepartmentRoleMutation();
  const [updateOrgRoles, { isLoading: isUpdatingOrg }] =
    useUpdateOrgUserRoleMutation();

  const departments = data?.departments ?? [];
  const ORGANIZATION = "organization";
  const [selectedDepartment, setSelectedDepartment] = useState(
    departmentId ?? ORGANIZATION,
  );
  const [role, setRole] = useState<GrantRole>(GrantRole.SourcingCreator);
  const [chosenPersonId, setChosenPersonId] = useState("");

  const candidates = (userData?.users ?? []).map((user) => ({
    id: user.person.id,
    name: `${user.person.firstName} ${user.person.lastName}`,
    organizationRoles: user.roles,
  }));
  const subject =
    person ?? candidates.find((candidate) => candidate.id === chosenPersonId);
  const personId = subject?.id ?? "";
  const personName = subject?.name ?? "";
  const organizationRoles = subject?.organizationRoles ?? [];

  const choosesWhere = departments.length > 1 && !departmentId;
  const isOrganization = selectedDepartment === ORGANIZATION;
  const department = departments.find((d) => d.id === selectedDepartment);
  const where = isOrganization
    ? t("the whole organization")
    : (department?.name ?? "");
  const roleOptions = isOrganization
    ? organizationAssignableRoles
    : departmentAssignableRoles;
  const superseded = isOrganization
    ? supersededOrganizationRoles(organizationRoles, role)
    : [];
  const isLoading = isAssigning || isUpdatingOrg;

  const submit = async () => {
    const result = isOrganization
      ? await updateOrgRoles({
          orgId,
          userId: personId,
          updateOrgUserRoleRequest: {
            newRoles: [
              ...new Set([
                ...organizationRoles,
                asOrganizationRole(role) as OrganizationRoleType,
              ]),
            ],
          },
        })
      : await assign({
          orgId,
          departmentId: selectedDepartment,
          departmentRoleRequest: { personId, role },
        });
    if ("data" in result) {
      toast({ title: t("Access given!"), status: "success" });
      onDone();
    } else {
      displayer.trigger(result.error);
    }
  };

  return (
    <SettingsCard title={t("Give access")}>
      <Flex columnGap="10" rowGap="3" flexWrap="wrap" alignItems="flex-end">
        {!person && (
          <FormControl maxW="xs">
            <FormLabel fontSize="xs" color="smMuted" mb="1">
              {t("Who")}
            </FormLabel>
            <Select
              size="sm"
              backgroundColor="smBackground"
              placeholder={t("Choose a person") ?? ""}
              value={chosenPersonId}
              onChange={(e) => setChosenPersonId(e.target.value)}
            >
              {candidates.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.name}
                </option>
              ))}
            </Select>
          </FormControl>
        )}
        {choosesWhere && (
          <FormControl maxW="xs">
            <FormLabel fontSize="xs" color="smMuted" mb="1">
              {t("Where")}
            </FormLabel>
            <Select
              size="sm"
              backgroundColor="smBackground"
              value={selectedDepartment}
              onChange={(e) => {
                setSelectedDepartment(e.target.value);
                setRole(GrantRole.SourcingCreator);
              }}
            >
              <option value={ORGANIZATION}>
                {t("The whole organization")}
              </option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl maxW="xs">
          <FormLabel fontSize="xs" color="smMuted" mb="1">
            {t("Role")}
          </FormLabel>
          <Select
            size="sm"
            backgroundColor="smBackground"
            value={role}
            onChange={(e) => setRole(e.target.value as GrantRole)}
          >
            {roleOptions.map((r) => (
              <option key={r} value={r}>
                {roleName(r)}
              </option>
            ))}
          </Select>
        </FormControl>
      </Flex>

      {subject && (
        <Text fontSize="sm">
          {t(
            choosesWhere || departmentId
              ? "{{person}} will have the role {{role}} in {{where}}."
              : "{{person}} will have the role {{role}}.",
            { person: personName, role: roleName(role), where },
          )}
        </Text>
      )}
      {superseded.length > 0 && (
        <Text fontSize="sm" color="smWarning">
          {t("This replaces {{roles}}.", {
            roles: superseded
              .map((r) => roleName(asGrantRole(r) as GrantRole))
              .join(", "),
          })}
        </Text>
      )}
      {!isOrganization && (
        <Text fontSize="xs" color="smMuted">
          {t(
            "Department roles only affect sourcing events, and only in this department.",
          )}
        </Text>
      )}

      <Flex columnGap="2">
        <Button
          size="sm"
          colorScheme="teal"
          isDisabled={!subject}
          isLoading={isLoading}
          onClick={submit}
        >
          {t("Give access")}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          {t("Cancel")}
        </Button>
      </Flex>
    </SettingsCard>
  );
};
